import type { AnyRouter } from '@tanstack/react-router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { APP_VERSION } from '../constants';
import { resolveEnv } from './sentry';

/**
 * Audience — self-hosted Matomo, no npm dependency (the standard `_paq` queue), no cookie.
 * The only file that knows Matomo: screens are tracked from the router, key actions call `trackEvent`.
 * Not to be confused with `api/tracking.ts` (business tracking of recos, on the back).
 */
const MATOMO_URL = 'https://analytics.coolify.racoreapp.com/';

/**
 * One Matomo site per environment of `resolveEnv()` and per kind of app, so QA never mixes with PROD nor the web
 * with the native app (one JS tracker, the same events everywhere) — local and unknown track nothing. The web sites
 * only accept their declared URL: every hit carries `canonical + path`, never `capacitor://localhost`.
 */
const MATOMO_SITES: Record<string, { web: string; app: string; canonical: string }> = {
  production: { web: '1', app: '3', canonical: 'https://web-app.racoreapp.com' },
  qa: { web: '2', app: '4', canonical: 'https://web-app-qa.racoreapp.com' },
};

/** Visit-scope custom dimensions, created with these ids on the four Matomo sites. */
const DIMENSION_PLATFORM = 1;
const DIMENSION_APP_VERSION = 2;

const HEARTBEAT_SECONDS = 15;
/** Matomo closes a visit after 30 min without a hit: a longer background stay is a new visit. */
const NEW_VISIT_AFTER_MS = 30 * 60 * 1000;

const INSTALL_ID_KEY = 'analytics.installId';
/** CNIL exemption for audience measurement: an identifier lives at most 13 months. */
const INSTALL_ID_LIFETIME_MS = 13 * 30 * 24 * 60 * 60 * 1000;

const CAMPAIGN_PARAM = /^(mtm_|utm_|pk_)/;

/** The queue before matomo.js loads — then matomo.js runs it and swaps `window._paq` for its own `{ push }`. */
type Paq = { push: (command: unknown[]) => unknown };
declare global {
  interface Window {
    _paq?: Paq;
  }
}

/** True only once analytics is on for this environment. */
let active = false;

/** Always through the current `window._paq`: a kept reference is the array matomo.js no longer reads. */
function track(...command: unknown[]): void {
  (window._paq ??= [] as unknown[][]).push(command);
}

/** The path as Matomo sees it: secret segments masked (an invite code lets anyone join the circle). */
function pagePath(pathname: string): string {
  return pathname.replace(/^\/invite\/[^/]+/, '/invite/:code');
}

/** Canonical origin + masked path + campaign params only (OAuth returns carry tokens in the query or hash). */
export function pageUrl(pathname: string, search: string, canonical: string): string {
  const campaign = [...new URLSearchParams(search)].filter(([key]) => CAMPAIGN_PARAM.test(key));
  const query = new URLSearchParams(campaign).toString();
  return canonical + pagePath(pathname) + (query ? `?${query}` : '');
}

/** A random id per install (16 hex chars, Matomo's visitor id format), renewed after 13 months. */
export async function installId(now = Date.now()): Promise<string> {
  const { value } = await Preferences.get({ key: INSTALL_ID_KEY });
  try {
    const saved = JSON.parse(value ?? '') as { id: string; createdAt: number };
    if (now - saved.createdAt < INSTALL_ID_LIFETIME_MS) return saved.id;
  } catch {
    // none yet, or unreadable: a new one below
  }
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  await Preferences.set({ key: INSTALL_ID_KEY, value: JSON.stringify({ id, createdAt: now }) });
  return id;
}

export async function initAnalytics(
  router: AnyRouter,
  environment = resolveEnv().environment,
): Promise<void> {
  active = false;
  const site = MATOMO_SITES[environment];
  if (!site) return;

  const visitorId = await installId();
  track('disableCookies'); // no consent banner needed
  track('setTrackerUrl', `${MATOMO_URL}matomo.php`);
  track('setSiteId', Capacitor.isNativePlatform() ? site.app : site.web);
  track('setVisitorId', visitorId);
  track('setCustomDimension', DIMENSION_PLATFORM, Capacitor.getPlatform());
  track('setCustomDimension', DIMENSION_APP_VERSION, APP_VERSION);
  track('enableLinkTracking');
  track('enableHeartBeatTimer', HEARTBEAT_SECONDS);
  active = true;

  let lastPath: string | null = null;
  const trackPage = (pathname: string, search: string, newVisit = false) => {
    const path = pagePath(pathname);
    if (path === lastPath && !newVisit) return;
    lastPath = path;
    track('setCustomUrl', pageUrl(pathname, search, site.canonical));
    track('setDocumentTitle', path);
    track('trackPageView');
  };

  trackPage(window.location.pathname, window.location.search);
  // The first load also resolves with pathChanged — lastPath keeps it to one view.
  router.subscribe('onResolved', ({ pathChanged, toLocation }) => {
    if (pathChanged) trackPage(toLocation.pathname, toLocation.searchStr);
  });

  if (Capacitor.isNativePlatform()) {
    let pausedAt: number | null = null;
    void App.addListener('pause', () => {
      pausedAt = Date.now();
    });
    void App.addListener('resume', () => {
      if (pausedAt !== null && Date.now() - pausedAt > NEW_VISIT_AFTER_MS) {
        trackPage(window.location.pathname, window.location.search, true);
      }
      pausedAt = null;
    });
  }

  // ponytail: offline at start → matomo.js never loads and the session is lost — retry on `online` if it matters.
  const script = document.createElement('script');
  script.async = true;
  script.src = `${MATOMO_URL}matomo.js`;
  document.head.appendChild(script);
}

/** A key action, after its success. A no-op where analytics is off (local, unknown). */
export function trackEvent(category: string, action: string, name?: string): void {
  if (!active) return;
  if (name === undefined) track('trackEvent', category, action);
  else track('trackEvent', category, action, name);
}
